import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
      const response = await fetch(`${API_URL}/blog`);
      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }
      const data = await response.json();
      setPosts(data);
    } catch (err) {
      setError('Failed to load blog posts. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Blog - SwiftToolsKit | Swift & SwiftUI Development Articles"
        description="Read our latest blog posts about Swift, SwiftUI, iOS development, and coding best practices."
        keywords="Swift blog, SwiftUI articles, iOS development blog, SwiftToolsKit blog"
        canonical="/blog"
        image="/swifttoolskitlogo.png"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'SwiftToolsKit Blog',
            description: 'Read our latest blog posts about Swift, SwiftUI, iOS development, and coding best practices.',
            url: typeof window !== 'undefined' ? `${window.location.origin}/blog` : 'https://swift-tools-kit.com/blog',
            publisher: {
              '@type': 'Organization',
              name: 'SwiftToolsKit',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: typeof window !== 'undefined' ? `${window.location.origin}/` : 'https://swift-tools-kit.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Blog',
                item: typeof window !== 'undefined' ? `${window.location.origin}/blog` : 'https://swift-tools-kit.com/blog',
              },
            ],
          },
        ]}
        type="website"
      />
      <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl font-bold mb-8">Blog</h1>
          
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">Loading blog posts...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">No blog posts available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post._id}
                  to={`/blog/${post.slug || post._id}`}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden group"
                >
                  {post.image && (
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 group-hover:text-primary-600 dark:group-hover:text-primary-500 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 line-clamp-3">
                      {post.description}
                    </p>
                    {post.createdAt && (
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
                        {new Date(post.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Blog;
